'use client'

import { useEffect, useRef, useState } from 'react'

import type { PortalTutorialTargetStatus } from '../interfaces/portal-tutorial-target-status.interface'
import { getPortalTutorialMeasurableElement } from '../utils/getPortalTutorialMeasurableElement'

const PORTAL_TUTORIAL_TARGET_RESOLVE_TIMEOUT_MS = 300

export function usePortalTutorialTargetElement(targetId: string | null, enabled: boolean) {
  const [element, setElement] = useState<HTMLElement | null>(null)
  const [status, setStatus] = useState<PortalTutorialTargetStatus>('pending')

  const activeTargetId = enabled ? targetId : null
  const previousTargetIdRef = useRef<string | null>(activeTargetId)

  if (previousTargetIdRef.current !== activeTargetId) {
    previousTargetIdRef.current = activeTargetId
    setStatus('pending')
    setElement(null)
  }

  useEffect(() => {
    if (!enabled || !targetId || typeof document === 'undefined') {
      setElement(null)
      setStatus('pending')
      return
    }

    setElement(null)
    setStatus('pending')

    let frameId = 0
    const startedAt = performance.now()

    const resolve = () => {
      const found = document.querySelector<HTMLElement>(`[data-portal-tutorial="${targetId}"]`)
      const measurable = getPortalTutorialMeasurableElement(found)

      if (measurable) {
        setElement((current) => (current === measurable ? current : measurable))
        setStatus('found')
        return
      }

      if (performance.now() - startedAt >= PORTAL_TUTORIAL_TARGET_RESOLVE_TIMEOUT_MS) {
        setElement(null)
        setStatus('missing')
        return
      }

      frameId = window.requestAnimationFrame(resolve)
    }

    resolve()

    return () => {
      window.cancelAnimationFrame(frameId)
    }
  }, [enabled, targetId])

  return { element, status }
}
